import Decimal from "decimal.js";
/** Scale for persisted monetary values (matches NUMERIC(18,2)). */
export const MONEY_SCALE = 2;
/** Configure Decimal.js for financial calculations. */
Decimal.set({
    precision: 20,
    rounding: Decimal.ROUND_HALF_UP,
    toExpNeg: -9,
    toExpPos: 21,
});
/**
 * Exact decimal money value. Never use JavaScript floating-point for financial math.
 * Persist and transmit as decimal strings (e.g. "125000.00").
 */
export class Money {
    value;
    constructor(value) {
        this.value = value;
    }
    static zero() {
        return new Money(new Decimal(0));
    }
    static from(input) {
        if (input instanceof Money) {
            return input;
        }
        if (input instanceof Decimal) {
            return new Money(input);
        }
        if (typeof input === "number") {
            if (!Number.isFinite(input)) {
                throw new Error("Money.from: input must be a finite number");
            }
            return new Money(new Decimal(input.toString()));
        }
        const trimmed = input.trim();
        if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
            throw new Error(`Money.from: invalid decimal string "${input}"`);
        }
        return new Money(new Decimal(trimmed));
    }
    /** Parse from database/API numeric string with fixed scale. */
    static fromDatabase(value) {
        return Money.from(value).toScale(MONEY_SCALE);
    }
    add(other) {
        return new Money(this.value.plus(other.value));
    }
    subtract(other) {
        return new Money(this.value.minus(other.value));
    }
    multiply(factor) {
        return new Money(this.value.times(new Decimal(factor.toString())));
    }
    isZero() {
        return this.value.isZero();
    }
    isPositive() {
        return this.value.gt(0);
    }
    isNegative() {
        return this.value.lt(0);
    }
    greaterThan(other) {
        return this.value.gt(other.value);
    }
    greaterThanOrEqual(other) {
        return this.value.gte(other.value);
    }
    lessThan(other) {
        return this.value.lt(other.value);
    }
    lessThanOrEqual(other) {
        return this.value.lte(other.value);
    }
    equals(other) {
        return this.value.eq(other.value);
    }
    /** Round to standard money scale (2 decimal places). */
    toScale(scale = MONEY_SCALE) {
        return new Money(this.value.toDecimalPlaces(scale, Decimal.ROUND_HALF_UP));
    }
    /** Serialize for API responses and JSON. */
    toString() {
        return this.toScale().value.toFixed(MONEY_SCALE);
    }
    /** Serialize for PostgreSQL NUMERIC columns. */
    toDatabaseString() {
        return this.toString();
    }
    toJSON() {
        return this.toString();
    }
}
/** Sum an array of money values exactly. */
export function sumMoney(values) {
    return values.reduce((total, value) => total.add(value), Money.zero());
}
/** Verify allocation lines reconcile exactly to a header total. */
export function allocationsMatchTotal(lineAmounts, headerTotal) {
    return sumMoney(lineAmounts).equals(headerTotal);
}
//# sourceMappingURL=decimal.js.map